import React from 'react';
import { Task } from '../../../../../types/task';

interface TaskTimeInfoProps {
  task: Task;
  formatDate: (date: string | null) => string | null;
  t: (key: string) => string;
  layout: 'separate' | 'combined'; // 控制布局方式
}

/**
 * 任务时间信息组件
 * 统一TaskItem和FocusTaskItem的时间信息显示，支持不同布局
 */
export const TaskTimeInfo: React.FC<TaskTimeInfoProps> = ({
  task,
  formatDate,
  t,
  layout
}) => {
  const hasTimeInfo = task.startAt || task.cost || task.dueAt;
  
  if (!hasTimeInfo) return null;

  if (layout === 'separate') {
    // TaskItem样式：分别显示各项时间信息
    return (
      <>
        {/* 开始时间 */}
        {task.startAt && (
          <div className="flex items-center mr-2">
            <svg className="w-3 h-3 mr-0.5" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 2.5L7 7L10 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="7" cy="7" r="5.25" stroke="currentColor"/>
            </svg>
            <span className="truncate">{formatDate(task.startAt)}</span>
          </div>
        )}

        {/* 预计耗时 */}
        {task.cost && (
          <div className="flex items-center mr-2">
            <svg className="w-3 h-3 mr-0.5" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 3.5C4.5 3.5 2.5 5.5 2.5 8" stroke="currentColor" strokeLinecap="round"/>
              <path d="M7 3.5C9.5 3.5 11.5 5.5 11.5 8" stroke="currentColor" strokeLinecap="round"/>
              <path d="M7 7L7 4.5" stroke="currentColor" strokeLinecap="round"/>
              <path d="M7 7L9 9" stroke="currentColor" strokeLinecap="round"/>
            </svg>
            <span className="truncate">
              {task.cost >= 86400
                ? t('duration.days', { count: Math.floor(task.cost / 86400) })
                : task.cost >= 3600
                  ? t('duration.hours', { count: Math.floor(task.cost / 3600) })
                  : t('duration.minutes', { count: Math.floor(task.cost / 60) })}
            </span>
          </div>
        )}

        {/* 截止日期 */}
        {task.dueAt && (
          <div className="flex items-center mr-2">
            <svg className="w-3 h-3 mr-0.5" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.5 4.5H11.5" stroke="currentColor" strokeLinecap="round"/>
              <path d="M11.5 4.5V10.5C11.5 11.0523 11.0523 11.5 10.5 11.5H3.5C2.94772 11.5 2.5 11.0523 2.5 10.5V4.5" stroke="currentColor" strokeLinecap="round"/>
              <path d="M4.5 2.5V6.5" stroke="currentColor" strokeLinecap="round"/>
              <path d="M9.5 2.5V6.5" stroke="currentColor" strokeLinecap="round"/>
            </svg>
            <span className="truncate">{formatDate(task.dueAt)}</span>
          </div>
        )}
      </>
    );
  } else {
    // FocusTaskItem样式：组合显示时间信息
    return (
      <span className="flex items-center mr-1.5">
        {task.startAt && (
          <span className="flex items-center">
            <svg className="w-3 h-3 mr-0.5 text-ios-gray3 dark:text-ios-dark-gray3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {formatDate(task.startAt)}
          </span>
        )}

        {task.cost && (
          <span className="ml-1">
            {t('time.minutesShort', { count: Math.floor(task.cost / 60) })}
          </span>
        )}

        {task.dueAt && (
          <span className="ml-1.5 flex items-center">
            <svg className="w-3 h-3 mr-0.5 text-ios-gray3 dark:text-ios-dark-gray3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('time.dueBy')} {formatDate(task.dueAt)}
          </span>
        )}
      </span>
    );
  }
};
